import { z } from 'zod'
import { DEFAULT_REST_SECONDS } from '../../lib/constants'
import { type HevyRoutineExercise, type HevyRoutineSet } from '../../services/hevyPushService'
import type { AITool } from './types'

// Set schema for individual sets - simplified for OpenAI strict mode
const setSchema = z.object({
  type: z
    .enum(['normal', 'warmup', 'failure', 'dropset'])
    .describe('Set type - normal, warmup, failure, or dropset'),
  reps: z
    .number()
    .int()
    .min(1)
    .max(100)
    .nullable()
    .describe('Number of reps (1-100), null for time/distance sets'),
  weight_kg: z
    .number()
    .min(0)
    .max(1000)
    .nullable()
    .describe('Weight in kilograms (0-1000kg), null for bodyweight'),
  duration_seconds: z
    .number()
    .int()
    .min(1)
    .max(7200)
    .nullable()
    .describe('Duration in seconds for time-based exercises, null for rep-based'),
  distance_meters: z
    .number()
    .min(0)
    .max(100000)
    .nullable()
    .describe('Distance in meters for cardio exercises, null for other types')
})

// Exercise schema for the routine
const exerciseSchema = z.object({
  exercise_template_id: z
    .string()
    .describe(
      'Exercise template ID from lookupExerciseTemplateID - ALWAYS lookup first, never guess'
    ),
  sets: z.array(setSchema).min(1).max(20).describe('Array of sets for this exercise (1-20 sets)'),
  superset_id: z
    .string()
    .nullable()
    .describe('Superset identifier to group exercises, null if not part of superset'),
  rest_seconds: z
    .number()
    .int()
    .min(30)
    .max(600)
    .nullable()
    .describe('Rest time between sets in seconds (30-600), null for default'),
  notes: z.string().nullable().describe('Exercise notes or coaching cues, null if none')
})

// Main parameters schema
const parametersSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(100)
    .describe('Routine name (e.g., "Push Day", "Upper Body Power", "Leg Day")'),
  exercises: z
    .array(exerciseSchema)
    .min(1)
    .max(20)
    .describe('Array of exercises for this routine (1-20 exercises)'),
  folder_id: z
    .number()
    .int()
    .nullable()
    .describe('Folder ID from createRoutineFolder to organize this routine, null for no folder'),
  notes: z
    .string()
    .nullable()
    .describe('Routine notes, coaching tips, or program information, null if none')
})

// Type definitions from the schemas
type SetInput = z.infer<typeof setSchema>
type ExerciseInput = z.infer<typeof exerciseSchema>

export const createRoutine: AITool = {
  name: 'createRoutine',
  description: 'Create workout routine with exercises.',
  parameters: parametersSchema,

  execute: async (args, _context) => {
    const { name, exercises, folder_id, notes } = args



    try {
      // Convert our exercise format to Hevy API format
      const hevyExercises: HevyRoutineExercise[] = exercises.map((exercise: ExerciseInput) => {
        // Convert sets to Hevy format - always use null for weight_kg
        const hevySets: HevyRoutineSet[] = exercise.sets.map((set: SetInput) => ({
          type: set.type,
          reps: set.reps,
          weight_kg: null, // Always null - users will fill in their own weights
          duration_seconds: set.duration_seconds,
          distance_meters: set.distance_meters,
          custom_metric: null
        }))

        return {
          exercise_template_id: exercise.exercise_template_id,
          superset_id: exercise.superset_id || null,
          rest_seconds: exercise.rest_seconds || DEFAULT_REST_SECONDS,
          notes: exercise.notes || '',
          sets: hevySets
        }
      })

      // Don't create the routine immediately - let user click "Add to Hevy" button
      // const routine = await createHevyRoutine(
      //   apiKey,
      //   name.trim(),
      //   hevyExercises,
      //   folder_id || undefined,
      //   notes || ""
      // )

      // Calculate total sets and estimated duration
      const totalSets = exercises.reduce(
        (total: number, exercise: ExerciseInput) => total + exercise.sets.length,
        0
      )
      const estimatedMinutes = exercises.reduce((total: number, exercise: ExerciseInput) => {
        const exerciseTime = exercise.sets.length * 2 // ~2 minutes per set (execution + rest)
        const restTime =
          (exercise.sets.length - 1) * ((exercise.rest_seconds || DEFAULT_REST_SECONDS) / 60) // Rest between sets
        return total + exerciseTime + restTime
      }, 0)

      // Return routine data for UI rendering (routine will be created when user clicks "Add to Hevy")
      const routineName = name ? name.trim() : 'Untitled Routine'

      return {
        type: 'routine',
        routineConfig: {
          id: `preview-${Date.now()}`,
          name: routineName,
          exercises: hevyExercises,
          folder_id: folder_id || undefined,
          notes: notes || '',
          totalSets,
          estimatedMinutes: Math.round(estimatedMinutes),
          created_at: new Date().toISOString()
        },
        summary: `Designed routine "${routineName}" with ${exercises.length} exercises and ${totalSets} total sets. Estimated duration: ${Math.round(estimatedMinutes)} minutes. Click "Add to Hevy" to save it.`
      }
    } catch (error) {
      console.error('Error creating routine:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        message: `Failed to create routine "${name.trim()}". Please check your exercise template IDs and try again.`
      }
    }
  }
}
